import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { RefreshCw } from 'lucide-react';

const PERIODS = [
  { label: 'Сегодня', value: '1d'  },
  { label: '7 дней',  value: '7d'  },
  { label: '30 дней', value: '30d' },
  { label: '90 дней', value: '90d' },
];

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n || 0);

function BarChart({ title, data, dataKey, max, color }) {
  return (
    <div className="card" style={{ padding: '20px' }}>
      <p style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' }}>
        {title}
      </p>
      {data?.length > 0 ? (
        <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: '100px' }}>
          {data.map((d, i) => {
            const pct = max > 0 ? d[dataKey] / max : 0;
            return (
              <div
                key={i}
                title={`${d.day}: ${fmt(d[dataKey])}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: '4px' }}
              >
                <div style={{
                  width: '100%',
                  background: color,
                  borderRadius: '3px 3px 0 0',
                  height: `${Math.max(2, pct * 80)}px`,
                  opacity: 0.75,
                  transition: 'height 0.4s var(--ease)',
                }} />
                <span style={{ fontSize: '9px', color: 'var(--text-3)', transform: 'rotate(-45deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                  {d.day?.slice(5)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty" style={{ padding: '24px 0', fontSize: '12px' }}>Нет данных</div>
      )}
    </div>
  );
}

export const Stats = () => {
  const { fetchAuthorized } = useAuth();
  const [period, setPeriod] = useState('7d');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAuthorized(`/api/dashboard/stats?period=${period}`);
      const j   = await res.json();
      if (j.success) setData(j);
    } catch {}
    finally { setLoading(false); }
  }, [fetchAuthorized, period]);

  useEffect(() => { load(); }, [load]);

  const t    = data?.totals;
  const maxR = data?.daily?.reduce((m, d) => Math.max(m, d.revenue), 0) || 1;
  const maxP = data?.daily?.reduce((m, d) => Math.max(m, d.pages),   0) || 1;

  const summary = [
    { label: 'Выручка',           value: `${fmt(t?.total_revenue)} UZS`, color: 'var(--yellow)' },
    { label: 'Оплаченных заказов', value: fmt(t?.paid_orders),            color: 'var(--green)'  },
    { label: 'Ч/Б листов',        value: fmt(t?.bw_pages),               color: null             },
    { label: 'Цветных листов',    value: fmt(t?.color_pages),            color: 'var(--brand)'  },
    { label: 'Ошибок',            value: fmt(t?.failed_orders),          color: t?.failed_orders > 0 ? 'var(--red)' : null },
  ];

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Статистика</h1>
          <p>Заказы и выручка по периодам</p>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`btn${period === p.value ? ' btn-primary' : ''}`}
              style={{ padding: '6px 12px' }}
            >
              {p.label}
            </button>
          ))}
          <button className="btn-icon" onClick={load} disabled={loading} title="Обновить" style={{ marginLeft: '2px' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {summary.map(({ label, value, color }) => (
          <div key={label} className="card stat-card">
            <div className="stat-value" style={{ fontSize: '18px', ...(color ? { color } : {}) }}>
              {value ?? '—'}
            </div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <BarChart title="Выручка по дням (UZS)" data={data?.daily} dataKey="revenue" max={maxR} color="var(--yellow)" />
        <BarChart title="Листов по дням"         data={data?.daily} dataKey="pages"   max={maxP} color="var(--brand)"  />
      </div>

      {/* Per-device */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: '600', fontSize: '13px' }}>
          По устройствам
        </div>
        <table>
          <thead>
            <tr>
              <th>Устройство</th>
              <th>Выручка</th>
              <th>Заказы</th>
              <th>Листов</th>
              <th style={{ width: '160px' }}>Доля</th>
            </tr>
          </thead>
          <tbody>
            {data?.by_device?.length > 0 ? data.by_device.map(d => {
              const maxDev = data.by_device.reduce((m, x) => Math.max(m, x.revenue), 0) || 1;
              const pct = Math.min(100, (d.revenue / maxDev) * 100);
              return (
                <tr key={d.device_id}>
                  <td>
                    <div style={{ fontWeight: '600', fontSize: '13px' }}>{d.machine_name || 'Unnamed'}</div>
                    <div className="text-dim mono" style={{ fontSize: '11px', marginTop: '1px' }}>{d.device_id?.slice(0, 12)}…</div>
                  </td>
                  <td>
                    <span style={{ fontWeight: '600' }}>{fmt(d.revenue)}</span>
                    <span className="text-dim" style={{ fontSize: '11px' }}> UZS</span>
                  </td>
                  <td className="text-muted">{fmt(d.orders)}</td>
                  <td className="text-muted">{fmt(d.pages)}</td>
                  <td>
                    <div style={{ height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: 'var(--brand)', borderRadius: '3px', transition: 'width 0.4s var(--ease)' }} />
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={5} className="empty">
                  {loading ? 'Загрузка...' : 'Нет данных за период'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
